import { STORAGE_KEY } from "@/constants";
import { NEXT_PUBLIC_BASE_URL } from "@/constants/enviroment";

/**
 * Singleton class responsible for managing client session tokens.
 * Handles storage, validation, and automatic refresh with retry logic.
 */
class ClientSessionManager {
  private static instance: ClientSessionManager;
  private isRefreshing = false;
  private refreshPromise: Promise<string | null> | null = null;
  private session: string | null = null;

  private constructor() {
    this.loadFromStorage();
  }

  // Ensures only one instance of the session manager exists
  public static getInstance(): ClientSessionManager {
    if (!ClientSessionManager.instance) {
      ClientSessionManager.instance = new ClientSessionManager();
    }
    return ClientSessionManager.instance;
  }

  // Loads session token from localStorage on initialization
  private loadFromStorage(): void {
    if (globalThis.window !== undefined) {
      this.session = localStorage.getItem(STORAGE_KEY);
    }
  }

  // Returns cached or stored session token
  public getSession(): string | null {
    if (!this.session && globalThis.window !== undefined) {
      this.session = localStorage.getItem(STORAGE_KEY);
    }
    return this.session;
  }

  // Validates whether a session token exists and is not empty
  public isValidSession(session?: string | null): boolean {
    const sessionToCheck = session || this.getSession();
    return !!(sessionToCheck && sessionToCheck.trim() !== "");
  }

  /**
   * Refreshes the client session.
   * Prevents concurrent refresh calls by reusing an existing promise if one is in progress.
   */
  public async refreshSession(retryCount = 0): Promise<string | null> {
    // If already refreshing, return the existing promise
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.performRefresh(retryCount);

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  // Performs the actual session refresh request with exponential backoff retries.
  private async performRefresh(retryCount: number): Promise<string | null> {
    try {
      const response = await fetch(
        `${NEXT_PUBLIC_BASE_URL}/api/tripla/client-session`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const newSession = data?.clientSession;

      if (!newSession) {
        throw new Error("No client session received from API");
      }

      this.setSession(newSession);
      return newSession;
    } catch (error) {
      console.error(
        `Failed to refresh client session (attempt ${retryCount + 1}):`,
        error,
      );

      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.performRefresh(retryCount + 1);
      }

      return null;
    }
  }

  // Saves the new session to both memory and localStorage
  public setSession(session: string): void {
    this.session = session;
    if (globalThis.window !== undefined) {
      localStorage.setItem(STORAGE_KEY, session);
    }
  }

  /**
   * Ensures a valid session exists.
   * If invalid or missing, triggers a refresh automatically.
   */
  public async ensureValidSession(): Promise<string | null> {
    const currentSession = this.getSession();

    if (this.isValidSession(currentSession)) {
      return currentSession;
    }

    console.log("No valid session found, refreshing...");
    return this.refreshSession();
  }
}

// Singleton instance export for global use
export const clientSessionManager = ClientSessionManager.getInstance();
